import { isStale } from './staleness.ts'
import { preferFact, type Citation, type Fact, type FactSource, type Origin, type SourceRank } from './provenance.ts'

/**
 * The live working state of a subject, as rows rather than as a document.
 *
 * One row per observation, appended and never revised. The reason is not
 * history, which snapshots already keep: it is that a single answers document
 * has to be read and written whole, so two writers touching different fields
 * silently overwrite one another. A payroll webhook filling in headcount at 3am
 * and a stock reading written a second earlier are not in conflict, and a store
 * that loses one of them is answering a question nobody asked.
 *
 * Generic over the field union and over the answers shape, because the row
 * arithmetic is the same whether the subject is one company or one household.
 * What differs is which fields exist and how long each lasts, and both of those
 * arrive in a `RecordShape`.
 */

/** A row as it goes in, minus whatever columns identify the subject. */
export interface Observation<F extends string> {
  field: F
  value: unknown
  source: FactSource
  rank: SourceRank
  /** The connection this came from, for a CONNECTOR fact. */
  source_ref?: string | null
  /** The system and organisation, as their customer would name them. */
  source_label?: string | null
  citation?: Citation | null
  observed_at: string
  stale_after?: string | null
}

/** A row as it comes back. `recorded_at` is the database's to stamp. */
export type Recorded<T> = T & { recorded_at: string }

/**
 * What an engine has to say about its own record before any of this can run.
 *
 * Three things, and deliberately no more: which fields exist, how to make an
 * empty set of answers, and how long each field's answer lasts. Everything else
 * about a domain stays in the domain.
 */
export interface RecordShape<F extends string, A> {
  fields: readonly F[]
  empty: () => A
  /** Months before a field's answer stops counting as current. */
  staleness: (field: F) => number | null
}

export function factOfRow<F extends string>(row: Observation<F>): Fact<unknown> {
  const origin: Origin | null = row.source_ref
    ? { ref: row.source_ref, ...(row.source_label ? { label: row.source_label } : {}) }
    : null

  return {
    value: row.value,
    source: row.source,
    rank: row.rank,
    observed_at: row.observed_at,
    ...(row.stale_after ? { stale_after: row.stale_after } : {}),
    ...(row.citation ? { citation: row.citation } : {}),
    ...(origin ? { origin } : {}),
  }
}

/**
 * The rows a subject's current answers are made of, resolved to one per field.
 *
 * Pure, and deliberately so: this is load-bearing and should be testable with
 * no database in the way. The caller does the query.
 *
 * Winner per field is `preferFact`, the same rule the write side has always
 * used: rank first, so a typed answer outranks a connected system and a subject
 * is never quietly corrected by a feed, with recency breaking ties.
 *
 * Staleness is applied here rather than left entirely to the engine, but only
 * as far as it has to be. A stale fact loses to any fresh one, so an old
 * self-report stops masking a reading taken since. Where every account of a
 * field is stale, the best of them is passed through rather than dropped, so
 * the engine still expires it and can say what went stale and when. Dropping it
 * here would leave the field merely absent, and an assessment that cannot tell
 * "never answered" from "answered too long ago" asks the wrong question next.
 */
export function materialize<F extends string, A>(
  rows: Recorded<Observation<F>>[],
  shape: RecordShape<F, A>,
  opts: { now?: string } = {},
): A {
  const now = Date.parse(opts.now ?? new Date().toISOString())
  const answers = shape.empty()

  const byField = new Map<F, Recorded<Observation<F>>[]>()
  for (const row of rows) {
    // A row for a field the questionnaire no longer has is not an error: it is
    // a question that was asked once and is not asked now. It stays in the
    // table and takes no part here.
    if (!shape.fields.includes(row.field)) continue
    const held = byField.get(row.field)
    if (held) held.push(row)
    else byField.set(row.field, [row])
  }

  for (const [field, held] of byField) {
    const months = shape.staleness(field)
    const facts = held.map(factOfRow)
    const fresh = facts.filter((f) => !isStale(f, months, now))
    const candidates = fresh.length > 0 ? fresh : facts

    const winner = candidates.reduce<Fact<unknown> | null>((best, f) => preferFact(best, f), null)
    if (winner) (answers as unknown as Record<string, unknown>)[field] = winner
  }

  return answers
}

/**
 * The rows to append for a change, which is every field whose account of itself
 * has moved.
 *
 * A field is written when its value, its source, or when it was observed
 * differs from what was already known. Comparing the value alone would drop a
 * connector confirming a figure the subject had already typed, and that is a
 * second observation of the same thing rather than nothing happening: it is
 * exactly what the read log is for.
 *
 * Unchanged fields produce nothing, so typing in one corner of a record does
 * not rewrite the rest of it.
 *
 * `ctx` is spread onto every row and is whatever identifies the subject. This
 * module never names those columns, which is why it can serve two records.
 */
export function changedFacts<F extends string, A, C extends object>(
  previous: A | null | undefined,
  current: A,
  ctx: C,
  shape: RecordShape<F, A>,
): (Observation<F> & C)[] {
  const out: (Observation<F> & C)[] = []
  const read = (a: A | null | undefined, field: F): Fact<unknown> | null =>
    a ? ((a as unknown as Record<string, unknown>)[field] as Fact<unknown> | null) ?? null : null

  for (const field of shape.fields) {
    const now = read(current, field)
    if (!now) continue

    const before = read(previous, field)
    const same =
      before &&
      before.source === now.source &&
      JSON.stringify(before.value) === JSON.stringify(now.value)

    if (same) {
      /**
       * A machine saying the same thing again is a second observation.
       *
       * A connector that re-reads a figure the record already held has
       * confirmed it against the system today, which resets its staleness and
       * is exactly what the read log exists to show. So the stamp alone makes
       * it a new row.
       *
       * A person is different, and telling the two apart is what this is for.
       * A typed answer that arrives again unchanged is not a second
       * observation: nobody re-affirmed anything, a component re-stamped a
       * value nobody touched. The record had four rows for one multi-select on
       * the demo business, two of them identical and two seconds apart with no
       * other value in between, which is the signature of a no-op rather than
       * of somebody changing their mind and changing it back.
       *
       * Costly in a way that compounds: fact volume grows with editing rather
       * than with screenings, and a row that says nothing still has to be read,
       * ranked and skipped on every materialisation for the life of the record.
       */
      if (now.source === 'USER') continue
      if (before.observed_at === now.observed_at) continue
    }

    out.push({
      ...ctx,
      field,
      value: now.value,
      source: now.source,
      rank: now.rank,
      observed_at: now.observed_at,
      stale_after: now.stale_after ?? null,
      citation: now.citation ?? null,
      // Which system reported it. Without this the account of a reading cannot
      // be derived and would have to be stored a second time.
      source_ref: now.origin?.ref ?? null,
      source_label: now.origin?.label ?? null,
    })
  }

  return out
}

/**
 * Rows that have just been written, folded into the answers on screen.
 *
 * For a page that asked a connector endpoint to take a reading and was handed
 * back the rows it wrote. The alternative is re-reading the whole record over
 * the network to learn two figures the reply already carried.
 *
 * These rows win outright rather than being ranked. Ranking is how a record
 * resolves several observations of one field, and that has already happened on
 * the other side, where the whole fact trail was in hand: the endpoint read the
 * record, decided what the reading displaced, and wrote the result. Running
 * `preferFact` again here would let a page overrule a decision made with more
 * information than the page has.
 *
 * Anything in the answers that is not a fact is untouched.
 */
export function applyFacts<F extends string, A>(
  answers: A,
  rows: Observation<F>[],
  shape: RecordShape<F, A>,
): A {
  const out = { ...answers }
  for (const row of rows) {
    if (!shape.fields.includes(row.field)) continue
    ;(out as unknown as Record<string, unknown>)[row.field] = factOfRow(row)
  }
  return out
}
